// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Checkbox } from '@mui/material';
import { Label } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import type { OnboardingQuestion } from '@shared/advisory/account-workflow/types';
import { useQuestionCheckboxes } from './use-question-checkboxes';

export interface OnboardingQuestionCheckboxesProps<TFormData extends FieldValues> {
  question: OnboardingQuestion;
  control: Control<TFormData>;
  name: Path<TFormData>;
}

export const QuestionCheckboxes = <TFormData extends FieldValues>({
  question,
  control,
  name,
}: OnboardingQuestionCheckboxesProps<TFormData>) => {
  const { isOptionChecked, handleToggle } = useQuestionCheckboxes({
    question,
    control: control as Control<FieldValues>,
    name: name as Path<FieldValues>,
  });

  return (
    <div data-testid={`question-${question.id}`}>
      <fieldset className="space-y-6">
        <Label
          component="legend"
          className="text-lg font-medium !leading-6 !text-secondary-dark"
          required
          aria-required="true"
        >
          <Translation
            i18nKey={question.label[0] as any}
            ns={question.label[1].ns}
          />
        </Label>

        <div className="flex flex-col gap-3">
          {question.options.map(option => (
            <Controller
              key={option.id}
              control={control}
              name={name}
              render={({ field }) => {
                const isChecked = isOptionChecked(option.value);

                return (
                  <div
                    className="flex items-center space-x-2"
                    data-testid={`option-${option.id}`}
                  >
                    <Checkbox
                      id={option.id}
                      data-testid={`checkbox-${option.id}`}
                      checked={isChecked}
                      onChange={() => handleToggle(option.value, field.onChange)}
                      className="!p-0"
                    />
                    <Label
                      htmlFor={option.id}
                      className="flex flex-1 cursor-pointer !text-base !font-normal !leading-6 !text-secondary-dark"
                      data-testid={`label-${option.id}`}
                    >
                      <Translation
                        i18nKey={option.label[0] as any}
                        ns={option.label[1].ns}
                      />
                    </Label>
                  </div>
                );
              }}
            />
          ))}
        </div>
      </fieldset>
    </div>
  );
};
